'use client'

import * as React from 'react'
import { useEffect } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import { ButtonSize } from '@/components/atomic/button/variants'
import { BodyXS } from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'
import { cn } from '@/utils/shadcn/utils'

interface ButtonMultiSelectOption {
  label: string
  value: string
  /** Optional tooltip text to show on hover */
  tooltip?: string
}

interface ButtonMultiSelectProps {
  value?: string[]
  onValueChange?: (value: string[]) => void
  defaultValue?: string[]
  options: ButtonMultiSelectOption[]
  className?: string
  label?: string
  isMultiSelect?: boolean
  columns?: number
  disabled?: boolean
  /** Button size - defaults to Small */
  buttonSize?: ButtonSize
  /**
   * Specifies the minimum number of options that must remain selected.
   * When a user attempts to deselect an option, the component will prevent the action
   * if it would cause the number of selected options to fall below this value.
   *
   * Example uses:
   * - Set to 1 to ensure at least one option is always selected
   * - Set to 0 (default) to allow all options to be deselected
   *
   * This works in both multi-select and single-select modes.
   */
  minSelected?: number
}

export function ButtonMultiSelect({
  value,
  onValueChange,
  defaultValue = [],
  options,
  className,
  label,
  isMultiSelect = true,
  columns = 2,
  disabled = false,
  buttonSize = ButtonSize.Large,
  minSelected = 0,
}: ButtonMultiSelectProps) {
  const [selectedOptions, setSelectedOptions] = React.useState<string[]>(
    value || defaultValue,
  )

  // Update internal state when controlled value changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedOptions(value)
    }
  }, [value])

  const handleSelect = (optionValue: string) => {
    let newSelection: string[]

    if (isMultiSelect) {
      // For multi-select mode
      if (selectedOptions.includes(optionValue)) {
        // Check if removing this value would violate minSelected constraint
        if (selectedOptions.length <= minSelected) {
          // Don't allow deselection if it would result in fewer than minSelected items
          return
        }
        newSelection = selectedOptions.filter((value) => value !== optionValue)
      } else {
        newSelection = [...selectedOptions, optionValue]
      }
    } else {
      // For single-select mode
      if (selectedOptions.includes(optionValue)) {
        // Check if deselecting this option would violate minSelected constraint
        if (minSelected > 0) {
          // In single-select mode with minSelected > 0, don't allow deselecting the only selected option
          return
        }
        // Allow deselection if minSelected is 0
        newSelection = []
      } else {
        // Selecting a new option in single-select mode
        newSelection = [optionValue]
      }
    }

    setSelectedOptions(newSelection)
    onValueChange?.(newSelection)
  }

  const gridColumns = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  }

  // Check if a button can be deselected (for styling)
  const cannotDeselect = (optionValue: string): boolean => {
    if (!selectedOptions.includes(optionValue)) {
      // Not selected, so not relevant
      return false
    }

    if (isMultiSelect) {
      // In multi-select, can't deselect if it would violate minSelected
      return selectedOptions.length <= minSelected
    } else {
      // In single-select, can't deselect if minSelected > 0
      return minSelected > 0
    }
  }

  return (
    <div className="w-full">
      {label && <BodyXS color={'text-secondary'}>{label}</BodyXS>}
      <div
        className={cn(
          'grid gap-2',
          gridColumns[columns as keyof typeof gridColumns] || gridColumns[2],
          className,
        )}
      >
        {options.map((option) => {
          const isSelected = selectedOptions.includes(option.value)
          const isNonDeselectable = cannotDeselect(option.value)

          const button = (
            <Button
              key={option.value}
              variant={
                isSelected ? ButtonVariant.Primary : ButtonVariant.Secondary
              }
              label={option.label}
              onClick={() => handleSelect(option.value)}
              size={buttonSize}
              className={cn(
                'w-full',
                'transition-opacity duration-350',
                isNonDeselectable && 'pointer-events-none opacity-90',
              )}
              disabled={disabled}
            />
          )

          if (option.tooltip) {
            return (
              <Tooltip key={option.value} simpleText={option.tooltip}>
                {button}
              </Tooltip>
            )
          }

          return button
        })}
      </div>
    </div>
  )
}
